import { archivosFetch } from '../../utils/fetch';
import { DocumentsPage, HomeDocument } from '../Home/types';
import { Pagination, SearchCriteria, toApiParams } from './criteria';

// The two advanced-search calls, over `GET /api/archives/search`
// and `/search/count`. Shapes verified with curl on 23/08 against the
// development stack:
//
//   /search        { documents, totalItems, page, limit } — each row with the
//                  same shape as the Home (without `file_path` nor `image_url`,
//                  see SearchResults about the grid thumbnail).
//   /search/count  { count }
//   400            { message } with a stable prefix: "Invalid issued_op",
//                  "Missing issued_from", "Invalid range", …
//   429            only on /count (120/min per account), with `Retry-After` in
//                  seconds.

const API = `${process.env.REACT_APP_API_URL}/api/archives`;

export const GENERIC_ERROR =
  "L'opération n'a pas pu aboutir. Veuillez réessayer dans quelques instants.";

export const THROTTLED_TEXT = 'Trop de requêtes, patientez un instant';

// How many times a 429 is retried before giving up, and how long to wait
// when the backend does not say how long.
const MAX_THROTTLE_RETRIES = 2;
const DEFAULT_RETRY_AFTER_SECONDS = 5;

export class SearchApiError extends Error {
  status: number;
  /** The backend message, in English, as-is. */
  raw: string;

  constructor(status: number, raw: string) {
    super(raw);
    this.name = 'SearchApiError';
    this.status = status;
    this.raw = raw;
  }
}

// A 400 is shown under the corresponding field, in French. The backend
// (services/documentSearch.js) names the parameter in the message, which
// is enough to know which field it is; the reason is recognised by the prefix.
export type FieldErrors = {
  issued?: string;
  archived?: string;
  /** What does not belong to any field. */
  form?: string;
};

const INVERTED_RANGE =
  'La date de fin doit être postérieure à la date de début.';
const INVALID_DATE = "La date n'est pas valide.";
const MISSING_DATE = 'Saisissez une date.';

export const fieldErrorsFrom = (error: unknown): FieldErrors => {
  if (!(error instanceof SearchApiError)) {
    return { form: GENERIC_ERROR };
  }

  if (error.status === 429) {
    return { form: THROTTLED_TEXT };
  }

  if (error.status !== 400) {
    return { form: GENERIC_ERROR };
  }

  const message = error.raw;
  const field: keyof FieldErrors | undefined = message.includes('issued_')
    ? 'issued'
    : message.includes('archived_')
    ? 'archived'
    : undefined;

  if (!field) return { form: 'Critères de recherche invalides.' };

  if (message.startsWith('Invalid range')) return { [field]: INVERTED_RANGE };
  if (message.startsWith('Missing')) return { [field]: MISSING_DATE };
  return { [field]: INVALID_DATE };
};

const readMessage = async (response: Response): Promise<string> => {
  try {
    const body = await response.json();
    return typeof body?.message === 'string' ? body.message : '';
  } catch (_) {
    return '';
  }
};

const parseRetryAfter = (header: string | null): number => {
  const seconds = Number(header);
  return Number.isFinite(seconds) && seconds > 0
    ? seconds
    : DEFAULT_RETRY_AFTER_SECONDS;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export type CountOptions = {
  /**
   * Called when the backend answers 429 and the call is waiting
   * for `Retry-After`; the counter shows "Trop de requêtes" in the meantime.
   */
  onThrottled?: (seconds: number) => void;
  signal?: AbortSignal;
};

export const fetchCount = async (
  criteria: SearchCriteria,
  options: CountOptions = {},
): Promise<number> => {
  const url = `${API}/search/count?${toApiParams(criteria).toString()}`;

  for (let attempt = 0; ; attempt += 1) {
    const response = await archivosFetch(url, { signal: options.signal });

    if (response.status === 429 && attempt < MAX_THROTTLE_RETRIES) {
      const seconds = parseRetryAfter(response.headers.get('Retry-After'));
      options.onThrottled?.(seconds);
      await sleep(seconds * 1000);
      continue;
    }

    if (!response.ok) {
      throw new SearchApiError(response.status, await readMessage(response));
    }

    const body = await response.json();
    return typeof body?.count === 'number' ? body.count : 0;
  }
};

export type SearchPage = DocumentsPage & Pagination;

export const fetchSearch = async (
  criteria: SearchCriteria,
  pagination: Pagination,
): Promise<SearchPage> => {
  const url = `${API}/search?${toApiParams(criteria, pagination).toString()}`;
  const response = await archivosFetch(url);

  if (!response.ok) {
    throw new SearchApiError(response.status, await readMessage(response));
  }

  const body = await response.json();

  return {
    documents: Array.isArray(body?.documents)
      ? (body.documents as HomeDocument[])
      : [],
    totalItems: typeof body?.totalItems === 'number' ? body.totalItems : 0,
    page: typeof body?.page === 'number' ? body.page : pagination.page,
    limit: typeof body?.limit === 'number' ? body.limit : pagination.limit,
  };
};
